import { useEffect } from 'react'

// Meta Pixel for the Polish landing page.
const PIXEL_ID = '1610093764138409'
const SCRIPT_ID = 'meta-pixel-script'

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void
  queue: unknown[]
  push: Fbq
  loaded: boolean
  version: string
}

declare global {
  interface Window {
    fbq?: Fbq
    _fbq?: Fbq
    __metaPixelInitialized?: boolean
  }
}

/**
 * Mirrors Meta's official base code: creates the fbq stub, loads fbevents.js,
 * then fires init + a single PageView. Guarded on window so it only ever runs
 * once per page load (no duplicate PageViews).
 */
function initMetaPixel(): void {
  if (typeof window === 'undefined' || !PIXEL_ID) return
  if (window.__metaPixelInitialized) return
  window.__metaPixelInitialized = true

  if (!window.fbq) {
    const fbq = function (this: unknown) {
      // eslint-disable-next-line prefer-rest-params
      const args = arguments
      if (fbq.callMethod) {
        fbq.callMethod.apply(fbq, args as unknown as unknown[])
      } else {
        fbq.queue.push(args)
      }
    } as unknown as Fbq

    fbq.push = fbq
    fbq.loaded = true
    fbq.version = '2.0'
    fbq.queue = []

    window.fbq = fbq
    if (!window._fbq) window._fbq = fbq
  }

  if (!document.getElementById(SCRIPT_ID)) {
    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.async = true
    script.src = 'https://connect.facebook.net/en_US/fbevents.js'
    const firstScript = document.getElementsByTagName('script')[0]
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript)
    } else {
      document.head.appendChild(script)
    }
  }

  window.fbq('init', PIXEL_ID)
  window.fbq('track', 'PageView')
}

// Fire as early as possible (on module load), not after first render.
initMetaPixel()

export function useMetaPixel(): void {
  useEffect(() => {
    initMetaPixel()
  }, [])
}

export function trackPixelEvent(event: string, params?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return
  window.fbq?.('track', event, params)
}
